<template>
  <div class="timestop-page">
    <div class="timestop-shell">
      <section class="page-hero">
        <div class="page-hero__copy">
          <span class="page-hero__icon" aria-hidden="true">
            <v-icon>mdi-timer-pause-outline</v-icon>
          </span>
          <div>
            <div class="app-eyebrow">Administration</div>
            <h1>Timestops</h1>
            <p>Anträge prüfen, Gebühren festlegen und den Bearbeitungsstatus zentral steuern.</p>
          </div>
        </div>
        <v-btn depressed class="hero-action" @click="createTimestopAsCustomer = true">
          <v-icon left>mdi-plus</v-icon>
          Timestop erfassen
        </v-btn>
      </section>

      <v-row dense class="summary-grid">
        <v-col cols="12" sm="4">
          <div class="summary-card">
            <span class="summary-card__icon"><v-icon>mdi-format-list-checks</v-icon></span>
            <div>
              <span>Sichtbare Anträge</span>
              <strong>{{ loading ? "–" : timestopsFiltered.length }}</strong>
              <small>mit aktuellen Filtern</small>
            </div>
          </div>
        </v-col>
        <v-col cols="12" sm="4">
          <div class="summary-card">
            <span class="summary-card__icon summary-card__icon--neutral"><v-icon>mdi-office-building-marker-outline</v-icon></span>
            <div>
              <span>Ausgewählte Studios</span>
              <strong>{{ selectedStudiosCount }}</strong>
              <small>im Arbeitsbereich</small>
            </div>
          </div>
        </v-col>
        <v-col cols="12" sm="4">
          <div class="summary-card">
            <span class="summary-card__icon summary-card__icon--status"><v-icon>mdi-progress-check</v-icon></span>
            <div>
              <span>Aktueller Status</span>
              <strong class="summary-card__status">{{ selectedStatusLabel }}</strong>
              <small>{{ timestops.length }} geladen</small>
            </div>
          </div>
        </v-col>
      </v-row>

      <section class="filter-surface">
        <div class="surface-heading">
          <div>
            <div class="app-eyebrow">Arbeitsbereich</div>
            <h2>Filter &amp; Schnellzugriff</h2>
          </div>
          <span class="result-count">
            {{ loading ? "Wird geladen …" : `${timestopsFiltered.length} ${timestopsFiltered.length === 1 ? "Ergebnis" : "Ergebnisse"}` }}
          </span>
        </div>

        <v-row dense class="filter-grid">
          <v-col cols="12" md="5">
            <v-select
              :loading="loading"
              :disabled="loading"
              label="Studios"
              :items="availableStudios"
              v-model="selectedStudios"
              prepend-inner-icon="mdi-map-marker-multiple-outline"
              multiple
              small-chips
              outlined
              dense
              dark
              hide-details></v-select>
          </v-col>
          <v-col cols="12" sm="5" md="3">
            <v-select
              @change="fetchTimestops()"
              :loading="loading"
              :disabled="loading"
              label="Status"
              :items="availableStates"
              v-model="selectedStatus"
              prepend-inner-icon="mdi-progress-check"
              outlined
              dense
              dark
              hide-details></v-select>
          </v-col>
          <v-col cols="12" sm="7" md="4">
            <v-text-field
              label="Vor- oder Nachname suchen"
              v-model="searchFilter"
              prepend-inner-icon="mdi-magnify"
              clearable
              outlined
              dense
              dark
              hide-details></v-text-field>
          </v-col>
        </v-row>

        <div class="quick-actions">
          <span class="quick-actions__label">Kundenansicht</span>
          <v-btn outlined class="quick-action" @click="createTimestopAsCustomer = true">
            <v-icon left small>mdi-account-plus-outline</v-icon>
            Timestop erstellen
          </v-btn>
          <v-btn outlined class="quick-action" v-if="userService.hasRight('d_v_timestops')" @click="showPaymentsForCustomer = true">
            <v-icon left small>mdi-cash-multiple</v-icon>
            Zahlungen
          </v-btn>
          <v-btn outlined class="quick-action" v-if="userService.hasRight('d_v_timestops')" @click="showTimestopCalculator = true">
            <v-icon left small>mdi-calculator-variant-outline</v-icon>
            Berechnen
          </v-btn>
        </div>
      </section>

      <section v-if="loading" class="loading-state" aria-live="polite">
        <v-progress-circular color="#009640" size="34" width="3" indeterminate></v-progress-circular>
        <div>
          <strong>Timestops werden geladen</strong>
          <span>Die aktuellen Anträge werden aufbereitet.</span>
        </div>
      </section>

      <section v-else-if="timestopsFiltered.length === 0" class="empty-state">
        <span class="empty-state__icon"><v-icon>mdi-file-search-outline</v-icon></span>
        <div>
          <h3>Keine Timestops gefunden</h3>
          <p>Mit den aktuellen Filtern gibt es keine Treffer. {{ timestops.length }} Timestops wurden insgesamt geladen.</p>
        </div>
      </section>

      <v-expansion-panels v-else dark flat class="timestop-panels">
        <v-expansion-panel v-for="timestop of timestopsFiltered" :key="timestop._id" class="timestop-panel">
          <v-expansion-panel-header class="timestop-panel__header">
            <div class="timestop-header">
              <span class="timestop-header__icon" aria-hidden="true"><v-icon>mdi-account-clock-outline</v-icon></span>
              <div class="timestop-header__copy">
                <strong>{{ timestop.customerInfos.firstName + " " + timestop.customerInfos.lastName }}</strong>
                <span>{{ timestop.studio.name }} · Eingereicht am {{ $moment(timestop.createdAt).format("DD.MM.YYYY") }}</span>
              </div>
              <div class="timestop-header__chips">
                <v-chip
                  small
                  dark
                  class="meta-chip"
                  :color="getTimestopDeadlineColor($moment(timestop.updatedAt).add(8, 'days').diff($moment(), 'days'))"
                  v-if="timestop.status === 'onHold'">
                  <v-icon left x-small>mdi-clock-alert-outline</v-icon>
                  {{ $moment(timestop.updatedAt).add(8, "days").diff($moment(), "days") + " Tage" }}
                </v-chip>
                <v-chip small class="meta-chip meta-chip--neutral" v-if="getOpenEntries(timestop).length">
                  <span v-for="(openEntry, index) of getOpenEntries(timestop)" :key="openEntry + index">
                    {{ index !== 0 ? " · " : "" }}{{ "1 " + timestopStateMap[openEntry] }}
                  </span>
                </v-chip>
              </div>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content class="timestop-panel__content">
            <div class="application-content">
              <div class="detail-grid">
                <section class="detail-section">
                  <div class="section-heading">
                    <span class="section-heading__icon"><v-icon>mdi-account-details-outline</v-icon></span>
                    <div>
                      <h3>Kundendaten</h3>
                      <p>Kontakt- und Adressinformationen</p>
                    </div>
                  </div>
                  <div class="info-list">
                    <div>
                      <v-icon>mdi-email-outline</v-icon><span>{{ timestop.customerInfos.email }}</span>
                    </div>
                    <div>
                      <v-icon>mdi-cake-variant-outline</v-icon><span>{{ timestop.customerInfos.birthday }}</span>
                    </div>
                    <div>
                      <v-icon>mdi-home-outline</v-icon><span>{{ timestop.customerInfos.street }}</span>
                    </div>
                    <div>
                      <v-icon>mdi-map-marker-outline</v-icon><span>{{ timestop.customerInfos.city + " " + timestop.customerInfos.zip }}</span>
                    </div>
                  </div>
                  <div class="signature-card">
                    <span>Unterschrift</span>
                    <v-img contain :src="timestop.customerInfos.signature" class="signature-image" alt="Unterschrift des Kunden"></v-img>
                  </div>
                </section>

                <section class="detail-section">
                  <div class="section-heading">
                    <span class="section-heading__icon"><v-icon>mdi-calendar-range-outline</v-icon></span>
                    <div>
                      <h3>Zeitraum</h3>
                      <p>Beantragte Unterbrechung</p>
                    </div>
                  </div>
                  <div class="period-card">
                    <div>
                      <span>Start</span>
                      <strong>{{ $moment(timestop.tacData.TimeSpan.Start).format("DD.MM.YYYY") }}</strong>
                    </div>
                    <v-icon>mdi-arrow-right</v-icon>
                    <div>
                      <span>Ende</span>
                      <strong>{{ $moment(timestop.tacData.TimeSpan.End).format("DD.MM.YYYY") }}</strong>
                    </div>
                  </div>
                  <div class="duration-pill">
                    <v-icon small>mdi-timer-sand</v-icon>
                    {{ timestop.tacData.Duration + " Tage" }}
                  </div>
                </section>
              </div>

              <section class="detail-section detail-section--full">
                <div class="section-heading">
                  <span class="section-heading__icon"><v-icon>mdi-paperclip</v-icon></span>
                  <div>
                    <h3>Dokumente</h3>
                    <p>{{ getGallery(timestop).length }} {{ getGallery(timestop).length === 1 ? "Anhang" : "Anhänge" }}</p>
                  </div>
                </div>
                <v-row dense v-if="getGallery(timestop).length">
                  <v-col
                    v-for="(galleryItem, index) of getGallery(timestop)"
                    cols="12"
                    sm="6"
                    md="4"
                    :key="`${galleryItem.src}-${galleryItem.sourceIndex}`">
                    <button
                      type="button"
                      class="attachment-card"
                      @click="openGalleryItem(timestop._id, galleryItem.sourceIndex, !isPdfGalleryItem(galleryItem))"
                      :aria-label="`${isPdfGalleryItem(galleryItem) ? 'PDF' : 'Bild'} ${index + 1} herunterladen`">
                      <span class="attachment-card__icon">
                        <v-icon>{{ isPdfGalleryItem(galleryItem) ? "mdi-file-pdf-box" : "mdi-image-outline" }}</v-icon>
                      </span>
                      <span>
                        <strong>{{ isPdfGalleryItem(galleryItem) ? "Anhang" : "Bild" }} {{ index + 1 }}</strong>
                        <small>Öffnen oder herunterladen</small>
                      </span>
                      <v-icon class="attachment-card__action">mdi-download-outline</v-icon>
                    </button>
                  </v-col>
                </v-row>
                <div v-else class="inline-empty">Keine Dokumente hinterlegt.</div>
              </section>

              <section class="detail-section detail-section--full">
                <div class="section-heading">
                  <span class="section-heading__icon"><v-icon>mdi-text-box-edit-outline</v-icon></span>
                  <div>
                    <h3>Antragsdetails</h3>
                    <p>Kommentare und Unterbrechungsgrund prüfen</p>
                  </div>
                </div>
                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-textarea
                      label="Kommentar Büro"
                      @blur="updateTimestopOfficeComment(timestop._id, timestop.officeComment)"
                      v-model="timestop.officeComment"
                      outlined
                      dense
                      dark
                      rows="3"></v-textarea>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-textarea label="Kommentar Kunde" disabled v-model="timestop.tacData.Comment" outlined dense dark rows="3"></v-textarea>
                  </v-col>
                  <v-col cols="12">
                    <v-select
                      mandatory
                      :items="tacReasonOptions"
                      v-model="timestop.tacData.ReasonId"
                      label="Grund des Unterbruchs"
                      prepend-inner-icon="mdi-clipboard-text-outline"
                      outlined
                      dense
                      dark
                      required></v-select>
                  </v-col>
                </v-row>
              </section>

              <section
                class="detail-section detail-section--full fee-section"
                v-if="timestop.status !== 'payed' && timestop.status !== 'accepted' && timestop.status !== 'onHold'">
                <div class="section-heading">
                  <span class="section-heading__icon"><v-icon>mdi-cash-check</v-icon></span>
                  <div>
                    <h3>Attest &amp; Gebühr</h3>
                    <p>Passende Gebührenoption für diesen Antrag festlegen</p>
                  </div>
                </div>
                <div class="fee-options" role="radiogroup" aria-label="Gebührenoption auswählen">
                  <div
                    class="fee-option"
                    :class="{ 'fee-option--active': timestop.withAttest && !timestop.officeChangedFee }"
                    role="radio"
                    tabindex="0"
                    :aria-checked="timestop.withAttest && !timestop.officeChangedFee"
                    @click="selectFeeOption(timestop, 'withAttest')"
                    @keyup.enter="selectFeeOption(timestop, 'withAttest')"
                    @keyup.space.prevent="selectFeeOption(timestop, 'withAttest')">
                    <span class="fee-option__check"><v-icon>mdi-check</v-icon></span>
                    <span>Mit Attest</span>
                    <strong>{{ timestop.fee.withAttest + " CHF" }}</strong>
                  </div>
                  <div
                    class="fee-option"
                    :class="{ 'fee-option--active': !timestop.withAttest && !timestop.officeChangedFee }"
                    role="radio"
                    tabindex="0"
                    :aria-checked="!timestop.withAttest && !timestop.officeChangedFee"
                    @click="selectFeeOption(timestop, 'withoutAttest')"
                    @keyup.enter="selectFeeOption(timestop, 'withoutAttest')"
                    @keyup.space.prevent="selectFeeOption(timestop, 'withoutAttest')">
                    <span class="fee-option__check"><v-icon>mdi-check</v-icon></span>
                    <span>Ohne Attest</span>
                    <strong>{{ timestop.fee.withoutAttest + " CHF" }}</strong>
                  </div>
                  <div
                    class="fee-option fee-option--custom"
                    :class="{ 'fee-option--active': timestop.officeChangedFee }"
                    role="radio"
                    tabindex="0"
                    :aria-checked="timestop.officeChangedFee"
                    @click="selectFeeOption(timestop, 'custom')"
                    @keyup.enter="selectFeeOption(timestop, 'custom')"
                    @keyup.space.prevent="selectFeeOption(timestop, 'custom')">
                    <span class="fee-option__check"><v-icon>mdi-check</v-icon></span>
                    <span>Neue Gebühr</span>
                    <v-text-field
                      v-model="timestop.officeChangedFeeAmount"
                      dark
                      type="Number"
                      suffix="CHF"
                      label="Betrag"
                      class="fee-input"
                      outlined
                      dense
                      hide-details
                      @click.stop="selectFeeOption(timestop, 'custom')"></v-text-field>
                  </div>
                </div>
                <v-row dense class="feedback-fields">
                  <v-col cols="12" md="5">
                    <v-select
                      v-model="preSelectableRejections"
                      label="Vorlage für Ablehnungsgrund"
                      prepend-inner-icon="mdi-message-alert-outline"
                      @change="changeRejectionValue($event, timestop)"
                      :items="rejectionItems"
                      outlined
                      dense
                      dark></v-select>
                  </v-col>
                  <v-col cols="12" md="7">
                    <v-textarea label="Rückmeldung an Kunden" v-model="timestop.officeNote" outlined dense dark rows="3"></v-textarea>
                  </v-col>
                </v-row>
              </section>

              <section v-else class="detail-section detail-section--full payment-section">
                <div class="section-heading">
                  <span class="section-heading__icon"><v-icon>mdi-cash-clock</v-icon></span>
                  <div>
                    <h3>{{ selectedStatus === "onHold" ? "Offene Zahlung" : "Zahlung" }}</h3>
                    <p>Gebühr und Zahlungsstatus verwalten</p>
                  </div>
                  <strong class="payment-amount">{{ timestop.tacData.IdlePeriodFee.value + " CHF" }}</strong>
                </div>
                <div class="payment-actions">
                  <v-btn v-if="selectedStatus === 'onHold'" depressed class="primary-action" @click="saveToTAC(timestop, 'payed')">
                    <v-icon left small>mdi-cash-check</v-icon>Als bezahlt markieren
                  </v-btn>
                  <v-btn
                    v-if="selectedStatus === 'onHold' && userService.hasRight('d_v_timestops')"
                    outlined
                    class="secondary-action"
                    @click="sendReminder(timestop)">
                    <v-icon left small>mdi-bell-outline</v-icon>Reminder senden
                  </v-btn>
                  <v-btn v-if="selectedStatus === 'onHold'" text class="danger-action" @click="setTimestopToLate(timestop)">
                    <v-icon left small>mdi-clock-alert-outline</v-icon>Zahlungsfrist abgelaufen
                  </v-btn>
                </div>
                <v-textarea
                  v-if="timestop.officeNote"
                  label="Rückmeldung an Kunden"
                  v-model="timestop.officeNote"
                  outlined
                  dense
                  dark
                  rows="3"></v-textarea>
              </section>

              <section v-if="timestop.tacPayResponse" class="invoice-banner">
                <span class="invoice-banner__icon"><v-icon>mdi-receipt-text-outline</v-icon></span>
                <div>
                  <span>TAC Rechnung</span>
                  <strong>Nr. {{ timestop.tacPayResponse.InvoiceNumber }}</strong>
                </div>
                <v-card v-if="false" @click="openPdf(timestop.tacPayResponse.ReceiptImage.MediaData, true)">
                  <v-card-title class="justify-center">Rechnung anzeigen</v-card-title>
                </v-card>
              </section>

              <section class="application-actions">
                <div class="application-actions__secondary">
                  <v-btn v-if="userService.hasRight('d_v_timestops')" text class="danger-action" @click="deleteTimestop(timestop)">
                    <v-icon left small>mdi-delete-outline</v-icon>Löschen
                  </v-btn>
                  <v-btn
                    v-if="selectedStatus === 'payed'"
                    outlined
                    class="secondary-action"
                    @click="updateStatusWithoutNotification(timestop, 'onHold')">
                    Zurück in Zahlungsaufforderung
                  </v-btn>
                  <v-btn v-if="selectedStatus === 'open'" outlined class="secondary-action" @click="editTimestopRange(timestop)">
                    <v-icon left small>mdi-calendar-edit-outline</v-icon>Zeitraum bearbeiten
                  </v-btn>
                </div>
                <div class="application-actions__primary" v-if="selectedStatus === 'open' || selectedStatus === 'payed'">
                  <v-btn v-if="selectedStatus !== 'payed'" text class="danger-action" @click="saveToTAC(timestop, 'rejected')">Ablehnen</v-btn>
                  <v-btn v-if="false" color="orange" :disabled="!isAcceptButtonEnabled(timestop)" @click="saveToTAC(timestop, 'onHold')">
                    Zurück an Kunden
                  </v-btn>
                  <v-btn
                    depressed
                    class="primary-action"
                    @click="timestop.status === 'open' ? saveToTAC(timestop, 'onHold') : saveToTAC(timestop, 'accepted')">
                    {{ timestop.status === "open" ? "Zahlungsaufforderung senden" : "Abschliessen und in TAC übertragen" }}
                  </v-btn>
                  <v-btn outlined class="primary-outline-action" @click="saveToTAC(timestop, 'acceptedNoSave')">Abschliessen ohne TAC</v-btn>
                </div>
              </section>

              <section class="manual-status" v-if="userService.hasRight('d_v_timestops')">
                <div>
                  <strong>Status manuell ändern</strong>
                  <span>Ohne Benachrichtigung an den Kunden</span>
                </div>
                <v-select label="Neuer Status" :items="availableStates" v-model="forceNewStatus" outlined dense dark hide-details></v-select>
                <v-btn
                  depressed
                  class="secondary-solid-action"
                  :disabled="!forceNewStatus || timestop.status === forceNewStatus"
                  @click="updateStatusWithoutNotification(timestop, forceNewStatus)">
                  <v-icon left small>mdi-content-save-outline</v-icon>Ändern
                </v-btn>
              </section>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <v-dialog
      v-model="timestopRangeEditActive"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      max-width="920"
      scrollable
      content-class="timestop-dialog">
      <v-card dark class="dialog-card" v-if="timestopRangeEdit && timestopRangeEdit.customerInfos && timestopRangeEdit.tacData">
        <v-card-title class="dialog-header">
          <div class="dialog-header__copy">
            <span class="dialog-header__icon"><v-icon>mdi-calendar-edit-outline</v-icon></span>
            <div>
              <div class="app-eyebrow">Antrag aktualisieren</div>
              <h2>Zeitraum anpassen</h2>
              <p>{{ timestopRangeEdit.customerInfos.firstName + " " + timestopRangeEdit.customerInfos.lastName }}</p>
            </div>
          </div>
          <v-btn icon class="icon-action" aria-label="Dialog schliessen" @click="timestopRangeEditActive = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider class="dialog-divider"></v-divider>
        <v-card-text class="dialog-content range-dialog-content">
          <div class="date-picker-grid">
            <section class="dialog-section">
              <div class="dialog-section__label"><v-icon small>mdi-calendar-start</v-icon>Startdatum</div>
              <v-date-picker
                full-width
                dark
                color="#009640"
                header-color="#262627"
                locale="de-CH"
                v-model="timestopRangeEdit.tacData.TimeSpan.Start"></v-date-picker>
            </section>
            <section class="dialog-section">
              <div class="dialog-section__label"><v-icon small>mdi-calendar-end</v-icon>Enddatum</div>
              <v-date-picker
                full-width
                dark
                color="#009640"
                header-color="#262627"
                locale="de-CH"
                v-model="timestopRangeEdit.tacData.TimeSpan.End"></v-date-picker>
            </section>
          </div>
          <section class="dialog-section fee-edit-section">
            <div>
              <strong>Gebühr festlegen</strong>
              <span>Der angepasste Betrag wird dem Kunden erneut zur Bestätigung vorgelegt.</span>
            </div>
            <v-text-field label="Gebühr" suffix="CHF" v-model="timeRangeEditFee" type="number" outlined dense dark hide-details></v-text-field>
          </section>
        </v-card-text>
        <v-divider class="dialog-divider"></v-divider>
        <v-card-actions class="dialog-footer">
          <v-spacer></v-spacer>
          <v-btn text class="secondary-action" @click="timestopRangeEditActive = false">Abbrechen</v-btn>
          <v-btn depressed class="primary-action" @click="saveEditedTimestopRange">
            <v-icon left small>mdi-send-outline</v-icon>Zurück an Kunden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      max-width="760"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      :value="fullSizeImage"
      persistent
      content-class="timestop-dialog"
      @click:outside="fullSizeImage = null">
      <v-card dark class="dialog-card image-dialog-card">
        <v-card-title class="dialog-header dialog-header--compact">
          <div class="dialog-header__copy">
            <span class="dialog-header__icon"><v-icon>mdi-image-outline</v-icon></span>
            <div><h2>Bildvorschau</h2></div>
          </div>
          <v-btn icon class="icon-action" aria-label="Bildvorschau schliessen" @click="fullSizeImage = null"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <div class="image-preview-surface">
          <v-img contain :src="fullSizeImage" class="full-size-image" alt="Vorschau des hochgeladenen Dokuments"></v-img>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog
      max-width="720"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      v-model="showPaymentsForCustomer"
      scrollable
      content-class="timestop-dialog">
      <v-card dark class="dialog-card embedded-dialog-card">
        <v-card-title class="dialog-header dialog-header--compact">
          <div class="dialog-header__copy">
            <span class="dialog-header__icon"><v-icon>mdi-cash-multiple</v-icon></span>
            <div>
              <div class="app-eyebrow">Kundenansicht</div>
              <h2>Zahlungen</h2>
            </div>
          </div>
          <v-btn icon class="icon-action" aria-label="Zahlungen schliessen" @click="showPaymentsForCustomer = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-card-title>
        <v-divider class="dialog-divider"></v-divider>
        <v-card-text class="embedded-dialog-content">
          <timestop-view-user-payments></timestop-view-user-payments>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      max-width="720"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      v-model="createTimestopAsCustomer"
      scrollable
      content-class="timestop-dialog">
      <v-card dark class="dialog-card embedded-dialog-card">
        <v-card-title class="dialog-header dialog-header--compact">
          <div class="dialog-header__copy">
            <span class="dialog-header__icon"><v-icon>mdi-account-plus-outline</v-icon></span>
            <div>
              <div class="app-eyebrow">Kundenansicht</div>
              <h2>Timestop erstellen</h2>
            </div>
          </div>
          <v-btn icon class="icon-action" aria-label="Kundenansicht schliessen" @click="createTimestopAsCustomer = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-card-title>
        <v-divider class="dialog-divider"></v-divider>
        <v-card-text class="embedded-dialog-content">
          <timestop-view-user-create-timestop v-if="createTimestopAsCustomer"></timestop-view-user-create-timestop>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog fullscreen v-model="showTimestopCalculator" content-class="timestop-dialog">
      <v-card dark class="dialog-card calculator-dialog-card">
        <v-card-title class="dialog-header dialog-header--compact">
          <div class="dialog-header__copy">
            <span class="dialog-header__icon"><v-icon>mdi-calculator-variant-outline</v-icon></span>
            <div>
              <div class="app-eyebrow">Hilfsmittel</div>
              <h2>Timestop berechnen</h2>
            </div>
          </div>
          <v-btn icon class="icon-action" aria-label="Calculator schliessen" @click="showTimestopCalculator = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-card-title>
        <v-divider class="dialog-divider"></v-divider>
        <v-card-text class="calculator-dialog-content">
          <timestop-calculator @close="showTimestopCalculator = false" v-if="showTimestopCalculator"></timestop-calculator>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import api from "@/api"
import timestopViewUserPayments from "./timestopViewUserPayments"
import timestopViewUserCreateTimestop from "./timestopviewUserCreateTimestop"
import timestopCalculator from "./timestopCalculator"
import userService from "../../../services/userService"

export default {
  components: {
    timestopViewUserPayments,
    timestopCalculator,
    timestopViewUserCreateTimestop,
  },
  data() {
    return {
      forceNewStatus: null,
      firstLoad: true,
      timestopRangeEdit: {},
      ignoreWatch: false,
      searchFilter: "",
      timestopRangeEditActive: false,
      showTimestopCalculator: false,
      preSelectableRejections: null,
      userService,
      createTimestopAsCustomer: false,
      showPaymentsForCustomer: false,
      tacReasons: null,
      loading: true,
      selectedStudios: [],
      timestops: [],
      fullSizeImage: null,
      selectedStatus: "open",
      availableStates: [
        {
          text: "Offen",
          value: "open",
        },
        {
          text: "Abgelehnt",
          value: "rejected",
        },
        {
          text: "Abgeschlossen",
          value: "accepted",
        },
        {
          text: "Zahlungsaufforderung",
          value: "onHold",
        },
        {
          text: "Zahlungsfrist abgelaufen",
          value: "toLate",
        },
        {
          text: "Bezahlt / Nicht eingetragen",
          value: "payed",
        },
        {
          text: "Durch Kunden abgelehnt",
          value: "rejectedCustomer",
        },
        {
          text: "Zeitraum angepasst",
          value: "timeRangeChanged",
        },
      ],
      rejectionItems: [
        {
          text: "Mehr als zwei Timestops",
          value: 0,
          rejectionText: "Du hast bereits zwei Timestops während der Laufzeit deines Abonnements eingelöst.",
        },
        {
          text: "Länger als 6 Monate",
          value: 1,
          rejectionText: "Du hattest bereits einen Timestop über eine Laufzeit von 6 Monaten.",
        },
        {
          text: "Laufzeit des Attests",
          value: 2,
          rejectionText: "Die Zeitdauer des Attests entspricht nicht der von dir gewählten Zeitdauer.",
        },
        {
          text: "Falsches Dokument",
          value: 3,
          rejectionText: "Leider entspricht das beigefügte Dokument nicht dem Grund für den Timestop.",
        },
      ],
      timestopStateMap: {
        open: "Offen",
        rejected: "Abgelehnt",
        onHold: "Zahlungsaufforderung",
        toLate: "Zahlungsfrist abgelaufen",
        payed: "Bezahlt / Nicht eingetragen",
        accepted: "Abgeschlossen",
        acceptedNoSave: "Abgeschlossen",
        timeRangeChanged: "Zeitraum angepasst",
        rejectedCustomer: "Durch Kunden abgelehnt",
      },
      timeRangeEditFee: null,
    }
  },
  created() {
    this.fetchTimestops()
    if (!this.userService.hasRight("d_v_timestops")) {
      this.selectedStatus = "onHold"
      this.availableStates = [
        {
          text: "Zahlungsaufforderung",
          value: "onHold",
        },
        {
          text: "Zahlungsfrist abgelaufen",
          value: "toLate",
        },
        {
          text: "Abgelehnt",
          value: "rejected",
        },
        {
          text: "Abgeschlossen",
          value: "accepted",
        },
      ]
    }
  },
  methods: {
    async updateStatusWithoutNotification(timestop, status) {
      if (!status) {
        this.$toast.error("Bitte wähle zuerst einen Status aus")
        return
      }
      timestop.status = status
      await api.updateTimeStopFormEntryNoNotification(timestop)
      this.$toast.success("Timestop wurde in neuen Status verschoben")
      this.fetchTimestops()
    },
    async updateTimestopOfficeComment(id, comment) {
      try {
        await api.updateTimestopOfficeComment(id, comment)
        this.$toast.success("Kommentar abgespeichert")
      } catch (e) {
        this.$toast.error("Fehler beim Abspeichern")
      }
    },
    getTimestopDeadlineColor(days) {
      return days > 3 ? "green" : days > -1 ? "orange" : "red"
    },
    getGallery(timestop) {
      const gallery = timestop && timestop.timestopInfos && Array.isArray(timestop.timestopInfos.gallery) ? timestop.timestopInfos.gallery : []
      return gallery
        .map((galleryItem, sourceIndex) => ({
          ...(galleryItem || {}),
          sourceIndex,
        }))
        .filter((galleryItem) => typeof galleryItem.src === "string" && galleryItem.src.trim())
    },
    isPdfGalleryItem(galleryItem) {
      if (!galleryItem || typeof galleryItem.src !== "string") return false
      return galleryItem.src.split(/[?#]/)[0].toLowerCase().endsWith(".pdf")
    },
    getOpenEntries(timestop) {
      return timestop && Array.isArray(timestop.allOpenEntries) ? timestop.allOpenEntries : []
    },
    selectFeeOption(timestop, option) {
      timestop.withAttest = option === "withAttest"
      timestop.officeChangedFee = option === "custom"
    },
    async sendReminder(timestop) {
      if (
        confirm(
          "Folgender Reminder wird gesendet\n Hallo " +
            timestop.customerInfos.firstName +
            ",\nwir möchten dich daran erinnern, dass dein Timestop-Antrag bereits genehmigt wurde. Damit wir den Timestop aktivieren können, fehlt noch die Begleichung der dafür anfallenden Gebühr.\nBitte denke daran, die Zahlung innerhalb der nächsten Tage auf Roody oder direkt in deinem Center vorzunehmen.\n Sportliche Grüsse\nDein well come Fit Team ",
        )
      ) {
        await api.sendReminderEmail(timestop)
      }
    },
    async openGalleryItem(id, index, image = false) {
      const dataURL = await api.fetchTimestopGalleryItemDataUrl(id, index)
      const link = document.createElement("a")
      link.href = dataURL
      if (image) {
        link.download = "bild.webp"
      } else {
        link.download = "dokument.pdf"
      }

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    changeRejectionValue(val, timestop) {
      timestop.officeNote = this.rejectionItems[val].rejectionText
    },
    async deleteTimestop(timestop) {
      if (confirm("Der Timestop wird unwiederruflich gelöscht. Der Kunde wird nicht kontaktiert")) {
        await api.deleteTimestopById(timestop._id)
        this.fetchTimestops()
      }
    },
    async editTimestopRange(timestop) {
      this.timestopRangeEdit = JSON.parse(JSON.stringify(timestop))
      this.timeRangeEditFee = JSON.parse(JSON.stringify(timestop.tacData.IdlePeriodFee.value))
      this.timestopRangeEditActive = true
    },
    async saveEditedTimestopRange() {
      if (confirm("Der Timestop geht mit angepasstem Zeitraum / Gebühr zurück an den Kunden und muss neu unterschrieben werden")) {
        const newVal = JSON.parse(JSON.stringify(this.timestopRangeEdit))

        newVal.tacData.IdlePeriodFee.value = this.timeRangeEditFee
        newVal.status = "timeRangeChanged"
        newVal.tacData.Duration =
          Number(this.$moment(newVal.tacData.TimeSpan.End, "YYYY-MM-DD").diff(this.$moment(newVal.tacData.TimeSpan.Start, "YYYY-MM-DD"), "days")) + 1

        await api.updateTimeStopFormEntry(newVal)

        this.fetchTimestops()
        this.timestopRangeEdit = null
        this.timestopRangeEditActive = false
      }
    },
    async setTimestopToLate(timestop) {
      if (
        confirm(
          "Der Timestop wird in die Rubrik Zahlungsfrist abgelaufen geschoben. Der Kunde wird per Mail informiert und kann den Timestop nicht mehr an seinem Handy zahlen.",
        )
      ) {
        await api.setTimestopToLate(timestop._id)
        this.fetchTimestops()
      }
    },

    isAcceptButtonEnabled(timestop) {
      // Initial Fee has changed

      if (timestop.officeChangedFee && timestop.officeChangedFeeAmount !== timestop.initialFee) return true

      // New Field clicked
      if (timestop.officeChangedFee && timestop.initialState !== "officeChangedFee") return true
      if (timestop.withAttest && timestop.initialState !== "withAttest") return true
      if (timestop.initialState === "withAttest" && !timestop.withAttest) return true
      if (timestop.initialState === "withoutAttest" && timestop.withAttest) return true
      return false
    },
    checkOfficeChangedFeeEntered(timestop) {
      if (timestop.officeChangedFee && timestop.officeChangedFeeAmount) this.$toast.error("Setze eine Gebühr fest")
    },
    async saveToTAC(timestop, state) {
      if (state === "rejected" && !timestop.officeNote)
        return this.$toast.error("Bitte schreibe dem Kunden eine Rückmeldung bevor du den Timestop ablehnst / zurück gibst")

      timestop.officeChangedFeeAmount = Number(timestop.officeChangedFeeAmount)
      let confirmSpan = "Der Timestop wird auf " + this.timestopStateMap[state] + " gesetzt."

      let confirmFee = 0
      if (timestop.withAttest) {
        confirmFee = timestop.fee.withAttest
      } else if (timestop.officeChangedFee) {
        confirmFee = timestop.officeChangedFeeAmount
      } else {
        confirmFee = timestop.fee.withoutAttest
      }
      if (state === "onHold") {
        confirmSpan =
          "Der Timestop wird akzeptiert und geht mit einer Zahlungsaufforderung zurück an den Kunden. \nEr wird noch nicht in TAC eingetragen \nDem Kunden werden " +
          confirmFee +
          " CHF in Rechnung gestellt."
      }

      if (state === "accepted") {
        confirmSpan = "Der Timestop des Kunden wir nun im TAC eingetragen und automatisch die dazugehörige Rechnung im TAC erstellt.."
      }
      if (state === "payed") {
        confirmSpan = "Der Timestop wird auf bezahlt gesetzt und ans Büro weitergeleitet"
      }
      if (confirm(confirmSpan)) {
        timestop.status = state

        timestop.tacData.IdlePeriodFee.value = confirmFee

        try {
          if (state === "accepted") {
            await api.createTACTimestop(timestop)
          }
          if (state === "acceptedNoSave") timestop.status = "accepted"
          await api.updateTimeStopFormEntry(timestop)
          this.fetchTimestops()
        } catch (e) {
          this.$toast.error(e.response.data)
        }
      }
    },
    async downloadPDF(id) {
      try {
        await api.fetchTimestopPDF(id)
      } catch (e) {}
    },
    async fetchTimestops() {
      this.loading = true
      try {
        this.tacReasons = await api.getTACReasons(1)
        const timestops = await api.fetchTimestopsAdmin(this.selectedStatus)
        this.timestops = await Promise.all(
          timestops.map(async (timestop) => {
            if (!timestop.changeLog) timestop.changeLog = []
            timestop.initialFee = JSON.parse(JSON.stringify(timestop.tacData.IdlePeriodFee.value))
            if (timestop.officeChangedFee) {
              timestop.officeChangedFeeAmount = timestop.tacData.IdlePeriodFee.value || 0
              timestop.initialState = "officeChangedFee"
            } else {
              timestop.initialState = timestop.withAttest ? "withAttest" : "withoutAttest"
            }
            timestop.changeLog.push(
              JSON.parse(
                JSON.stringify({
                  state: timestop.initialState,
                  fee: timestop.initialFee,
                  note: timestop.officeNote,
                }),
              ),
            )
            return timestop
          }),
        )

        if (this.firstLoad) {
          const currentStudio = this.userService.getStudio()
          const currentStudioName = currentStudio && currentStudio.name
          this.selectedStudios = currentStudioName && this.studioNames.includes(currentStudioName) ? [currentStudioName] : [...this.studioNames]
        }
        this.firstLoad = false
      } catch (e) {
        console.error("Fehler beim Laden der Timestops:", e)
        this.$toast.error("Fehler beim laden")
      } finally {
        this.loading = false
      }
    },
  },
  computed: {
    selectedStatusLabel() {
      const status = this.availableStates.find((entry) => entry.value === this.selectedStatus)
      return status ? status.text : this.selectedStatus
    },
    selectedStudiosCount() {
      const selectedStudios = Array.isArray(this.selectedStudios) ? this.selectedStudios : []
      return new Set(selectedStudios.filter((studio) => typeof studio === "string" && studio !== "all")).size
    },
    timestopsFiltered() {
      const timestops = Array.isArray(this.timestops) ? this.timestops : []
      const selectedStudios = Array.isArray(this.selectedStudios) ? this.selectedStudios : []
      const query = String(this.searchFilter || "")
        .trim()
        .toLowerCase()

      return timestops.filter((timestop) => {
        const studioName = timestop && timestop.studio && timestop.studio.name
        if (!studioName || !selectedStudios.includes(studioName)) return false
        if (!query) return true

        const customer = timestop.customerInfos || {}
        return [customer.firstName, customer.lastName].filter(Boolean).join(" ").toLowerCase().includes(query)
      })
    },
    studioNames() {
      const timestops = Array.isArray(this.timestops) ? this.timestops : []
      return [...new Set(timestops.map((timestop) => timestop && timestop.studio && timestop.studio.name).filter(Boolean))].sort()
    },
    availableStudios() {
      const selectedStudios = Array.isArray(this.selectedStudios) ? this.selectedStudios : []
      const allSelected = this.studioNames.length > 0 && this.studioNames.every((studio) => selectedStudios.includes(studio))
      return [{ text: allSelected ? "Alle abwählen" : "Alle auswählen", value: "all" }, ...this.studioNames]
    },
    tacReasonOptions() {
      const reasons =
        this.tacReasons && this.tacReasons.Reasons && Array.isArray(this.tacReasons.Reasons.Reason) ? this.tacReasons.Reasons.Reason : []

      return reasons
        .map((reason) => ({
          text: reason && reason.ReasonCode,
          value: reason && reason.ReasonID && reason.ReasonID.UniqueID,
        }))
        .filter((reason) => reason.text && reason.value !== undefined && reason.value !== null)
    },
  },
  watch: {
    selectedStudios(newVal, oldVal) {
      if (this.ignoreWatch) {
        this.ignoreWatch = false
        return
      }

      const currentSelection = Array.isArray(newVal) ? newVal : []
      const previousSelection = Array.isArray(oldVal) ? oldVal : []
      const isAllOption = (studio) => studio === "all" || (studio && studio.value === "all")
      const addedAll = currentSelection.some(isAllOption) && !previousSelection.some(isAllOption)

      if (!addedAll) return

      const hadEveryStudioSelected = this.studioNames.length > 0 && this.studioNames.every((studio) => previousSelection.includes(studio))
      this.ignoreWatch = true
      this.selectedStudios = hadEveryStudioSelected ? [] : [...this.studioNames]
    },
  },
}
</script>

<style lang="scss" scoped>
.timestop-page {
  --ts-primary: #009640;
  --ts-primary-hover: #00a848;
  --ts-primary-soft: rgba(0, 150, 64, 0.14);
  --ts-border: rgba(255, 255, 255, 0.1);
  --ts-border-strong: rgba(255, 255, 255, 0.14);
  --ts-text: #f4f4f5;
  --ts-muted: #9b9ba0;
  min-height: 100%;
  padding: clamp(14px, 2.4vw, 34px);
  color: var(--ts-text);
  background: transparent;
}

.timestop-shell {
  width: 100%;
  max-width: 1360px;
  margin: 0 auto;
}

.page-hero {
  position: relative;
  display: flex;
  min-height: 148px;
  overflow: hidden;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: clamp(24px, 3vw, 36px);
  border: 1px solid var(--ts-border-strong);
  border-radius: 24px;
  background: radial-gradient(circle at 91% 8%, rgba(0, 150, 64, 0.1), transparent 34%),
    linear-gradient(120deg, rgba(0, 0, 0, 0.3), rgba(255, 255, 255, 0.025));
  box-shadow: 0 22px 64px rgba(0, 0, 0, 0.22);
  backdrop-filter: blur(18px);
}

.page-hero__copy,
.dialog-header__copy {
  position: relative;
  z-index: 1;
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 16px;
}

.page-hero__icon,
.dialog-header__icon,
.empty-state__icon,
.summary-card__icon,
.timestop-header__icon,
.section-heading__icon,
.invoice-banner__icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 150, 64, 0.3);
  color: #d8f3e2;
  background: var(--ts-primary-soft);
}

.page-hero__icon {
  width: 54px;
  height: 54px;
  border-radius: 17px;
}

.page-hero__icon .v-icon,
.dialog-header__icon .v-icon,
.empty-state__icon .v-icon,
.summary-card__icon .v-icon,
.timestop-header__icon .v-icon,
.section-heading__icon .v-icon,
.invoice-banner__icon .v-icon {
  color: inherit;
}

.page-hero__icon .v-icon {
  font-size: 28px;
}

.app-eyebrow {
  margin-bottom: 5px;
  color: #65ca8b;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.14em;
  line-height: 1.2;
  text-transform: uppercase;
}

.page-hero h1 {
  margin: 0;
  color: #ffffff;
  font-size: clamp(27px, 3vw, 36px);
  font-weight: 760;
  letter-spacing: -0.035em;
  line-height: 1.12;
}

.page-hero p {
  max-width: 660px;
  margin: 7px 0 0;
  color: #a6a6ab;
  font-size: 14px;
  line-height: 1.5;
}

.hero-action,
.primary-action,
.primary-outline-action,
.secondary-action,
.secondary-solid-action,
.danger-action,
.quick-action {
  min-height: 44px;
  border-radius: 13px;
  font-weight: 700;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

.hero-action,
.primary-action {
  color: #ffffff !important;
  background: var(--ts-primary) !important;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.22);
}

.hero-action {
  z-index: 1;
  flex: 0 0 auto;
  min-height: 48px;
  padding-inline: 20px !important;
}

.hero-action:hover,
.hero-action:focus-visible,
.primary-action:hover,
.primary-action:focus-visible {
  background: var(--ts-primary-hover) !important;
}

.summary-grid {
  margin-top: 14px;
}

.summary-card {
  display: flex;
  min-height: 108px;
  height: 100%;
  align-items: center;
  gap: 15px;
  padding: 18px;
  border: 1px solid var(--ts-border);
  border-radius: 19px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.045), rgba(0, 0, 0, 0.17));
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(16px);
}

.summary-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
}

.summary-card__icon--neutral {
  border-color: rgba(255, 255, 255, 0.12);
  color: #c8c8cc;
  background: rgba(255, 255, 255, 0.06);
}

.summary-card__icon--status {
  border-color: rgba(0, 150, 64, 0.25);
  color: #72d697;
  background: rgba(0, 150, 64, 0.11);
}

.summary-card > div {
  display: grid;
  min-width: 0;
  grid-template-columns: auto 1fr;
  align-items: baseline;
  gap: 3px 8px;
}

.summary-card span {
  grid-column: 1 / -1;
  color: var(--ts-muted);
  font-size: 11px;
  font-weight: 650;
}

.summary-card strong {
  color: #ffffff;
  font-size: 27px;
  font-weight: 760;
  letter-spacing: -0.035em;
  line-height: 1.1;
}

.summary-card strong.summary-card__status {
  overflow: hidden;
  max-width: 180px;
  font-size: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.summary-card small {
  overflow: hidden;
  color: #a7a7ac;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-surface {
  margin-top: 14px;
  padding: 20px;
  border: 1px solid var(--ts-border);
  border-radius: 21px;
  background: rgba(0, 0, 0, 0.18);
  box-shadow: 0 16px 44px rgba(0, 0, 0, 0.16);
  backdrop-filter: blur(18px);
}

.surface-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.surface-heading h2 {
  margin: 0;
  color: var(--ts-text);
  font-size: 18px;
  font-weight: 730;
  letter-spacing: -0.015em;
}

.result-count {
  padding: 7px 11px;
  border: 1px solid rgba(0, 150, 64, 0.24);
  border-radius: 999px;
  color: #ccebd8;
  background: rgba(0, 150, 64, 0.1);
  font-size: 11px;
  font-weight: 750;
  white-space: nowrap;
}

.filter-grid {
  margin-bottom: 0 !important;
}

.filter-surface ::v-deep .v-input__slot,
.application-content ::v-deep .v-input__slot,
.dialog-card ::v-deep .v-input__slot {
  min-height: 44px !important;
  border-radius: 13px !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

.filter-surface ::v-deep .v-text-field--outlined fieldset,
.application-content ::v-deep .v-text-field--outlined fieldset,
.dialog-card ::v-deep .v-text-field--outlined fieldset {
  border-color: rgba(255, 255, 255, 0.14);
  transition: border-color 160ms ease, box-shadow 160ms ease;
}

.filter-surface ::v-deep .v-input--is-focused fieldset,
.application-content ::v-deep .v-input--is-focused fieldset,
.dialog-card ::v-deep .v-input--is-focused fieldset {
  border-width: 1px;
  border-color: var(--ts-primary) !important;
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.12);
}

.filter-surface ::v-deep .v-label,
.application-content ::v-deep .v-label,
.dialog-card ::v-deep .v-label {
  color: #9b9ba0;
}

.filter-surface ::v-deep .v-chip {
  border: 1px solid rgba(0, 150, 64, 0.2);
  color: #ccebd8;
  background: rgba(0, 150, 64, 0.11) !important;
}

.filter-surface ::v-deep .v-progress-linear.primary--text,
.application-content ::v-deep .v-progress-linear.primary--text,
.dialog-card ::v-deep .v-progress-linear.primary--text {
  color: var(--ts-primary) !important;
}

.quick-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
}

.quick-actions__label {
  margin-right: 4px;
  color: #88888d;
  font-size: 11px;
  font-weight: 750;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.quick-action {
  border-color: rgba(255, 255, 255, 0.13) !important;
  color: #c6c6ca !important;
  background: rgba(255, 255, 255, 0.025);
}

.quick-action:hover,
.quick-action:focus-visible {
  border-color: rgba(0, 150, 64, 0.36) !important;
  color: #e8f7ed !important;
  background: rgba(0, 150, 64, 0.11);
}

.loading-state,
.empty-state {
  display: flex;
  min-height: 150px;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-top: 14px;
  padding: 26px;
  border: 1px dashed rgba(255, 255, 255, 0.13);
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.14);
  text-align: left;
}

.loading-state > div,
.empty-state > div {
  display: grid;
  gap: 3px;
}

.loading-state strong,
.empty-state h3 {
  margin: 0;
  color: #e9e9eb;
  font-size: 15px;
  font-weight: 720;
}

.loading-state span,
.empty-state p {
  margin: 0;
  color: #939398;
  font-size: 12px;
}

.empty-state__icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
}

.timestop-panels {
  display: grid;
  gap: 11px;
  margin-top: 14px;
}

.timestop-panel {
  overflow: hidden;
  border: 1px solid var(--ts-border);
  border-radius: 19px !important;
  color: var(--ts-text);
  background: rgba(0, 0, 0, 0.18) !important;
  box-shadow: 0 14px 38px rgba(0, 0, 0, 0.16);
}

.timestop-panel::before {
  box-shadow: none !important;
}

.timestop-panel__header {
  min-height: 82px;
  padding: 14px 18px !important;
}

.timestop-panel__header:hover {
  background: rgba(255, 255, 255, 0.035);
}

.timestop-panel__header ::v-deep .v-expansion-panel-header__icon .v-icon {
  color: #8d8d92;
}

.timestop-header {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 13px;
}

.timestop-header__icon {
  width: 42px;
  height: 42px;
  border-radius: 14px;
}

.timestop-header__copy {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.timestop-header__copy strong {
  overflow: hidden;
  color: #f1f1f2;
  font-size: 15px;
  font-weight: 730;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.timestop-header__copy span {
  overflow: hidden;
  color: #96969b;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.timestop-header__chips {
  display: flex;
  margin-left: auto;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 6px;
}

.meta-chip {
  height: 28px !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px !important;
  font-size: 10px !important;
  font-weight: 750;
}

.meta-chip--neutral {
  color: #c7c7cb !important;
  background: #3a3a3c !important;
}

.timestop-panel__content ::v-deep .v-expansion-panel-content__wrap {
  padding: 0;
}

.application-content {
  display: grid;
  gap: 12px;
  padding: 0 18px 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
  background: rgba(0, 0, 0, 0.08);
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding-top: 16px;
}

.detail-section {
  min-width: 0;
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 17px;
  background: rgba(255, 255, 255, 0.03);
}

.detail-section--full {
  width: 100%;
}

.section-heading {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 15px;
}

.section-heading__icon {
  width: 34px;
  height: 34px;
  border-radius: 11px;
}

.section-heading__icon .v-icon {
  font-size: 18px;
}

.section-heading > div {
  min-width: 0;
}

.section-heading h3 {
  margin: 1px 0 0;
  color: #ececee;
  font-size: 14px;
  font-weight: 730;
  line-height: 1.3;
}

.section-heading p {
  margin: 3px 0 0;
  color: #8e8e93;
  font-size: 11px;
  line-height: 1.4;
}

.info-list {
  display: grid;
  gap: 9px;
}

.info-list > div {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 9px;
  color: #c5c5c9;
  font-size: 12px;
}

.info-list .v-icon {
  color: #7f7f84;
  font-size: 17px;
}

.info-list span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.signature-card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  min-height: 76px;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding: 9px 12px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 13px;
  color: #8e8e93;
  background: rgba(0, 0, 0, 0.14);
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.signature-image {
  width: 100%;
  height: 58px;
  border-radius: 8px;
  background: #d8d8d8;
}

.period-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  padding: 14px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 14px;
  background: rgba(0, 0, 0, 0.14);
}

.period-card > div {
  display: grid;
  gap: 3px;
}

.period-card > div:last-child {
  text-align: right;
}

.period-card span {
  color: #85858a;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
}

.period-card strong {
  color: #e1e1e3;
  font-size: 13px;
}

.period-card .v-icon {
  color: #6f6f74;
}

.duration-pill {
  display: inline-flex;
  min-height: 34px;
  margin-top: 12px;
  padding: 0 11px;
  align-items: center;
  gap: 7px;
  border: 1px solid rgba(0, 150, 64, 0.22);
  border-radius: 999px;
  color: #ccebd8;
  background: rgba(0, 150, 64, 0.09);
  font-size: 11px;
  font-weight: 720;
}

.duration-pill .v-icon {
  color: #65ca8b;
}

.attachment-card {
  display: grid;
  width: 100%;
  min-height: 66px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 11px;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 14px;
  outline: none;
  color: inherit;
  background: rgba(0, 0, 0, 0.13);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease, transform 160ms ease;
}

.attachment-card:hover,
.attachment-card:focus-visible {
  border-color: rgba(0, 150, 64, 0.35);
  background: rgba(0, 150, 64, 0.08);
  transform: translateY(-1px);
}

.attachment-card__icon {
  display: inline-flex;
  width: 38px;
  height: 38px;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  color: #d8f3e2;
  background: rgba(0, 150, 64, 0.12);
}

.attachment-card__icon .v-icon {
  color: inherit;
  font-size: 20px;
}

.attachment-card > span:nth-child(2) {
  display: grid;
  min-width: 0;
  gap: 2px;
}

.attachment-card strong {
  overflow: hidden;
  color: #dedee0;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-card small {
  color: #88888d;
  font-size: 10px;
}

.attachment-card__action {
  color: #65ca8b !important;
  font-size: 19px !important;
}

.inline-empty {
  padding: 18px;
  border: 1px dashed rgba(255, 255, 255, 0.11);
  border-radius: 13px;
  color: #85858a;
  background: rgba(0, 0, 0, 0.1);
  font-size: 12px;
  text-align: center;
}

.fee-options {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.fee-option {
  position: relative;
  display: grid;
  min-height: 126px;
  align-content: center;
  justify-items: center;
  gap: 5px;
  padding: 16px 12px;
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: 15px;
  outline: none;
  color: #ababaf;
  background: rgba(0, 0, 0, 0.14);
  text-align: center;
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease, transform 160ms ease;
}

.fee-option:hover,
.fee-option:focus-visible {
  border-color: rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.045);
  transform: translateY(-1px);
}

.fee-option--active {
  border-color: rgba(0, 150, 64, 0.5);
  color: #eaf7ef;
  background: rgba(0, 150, 64, 0.11);
  box-shadow: inset 0 0 0 1px rgba(0, 150, 64, 0.12);
}

.fee-option__check {
  position: absolute;
  top: 10px;
  right: 10px;
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 50%;
  color: transparent;
  background: rgba(0, 0, 0, 0.2);
}

.fee-option__check .v-icon {
  color: inherit;
  font-size: 15px;
}

.fee-option--active .fee-option__check {
  border-color: var(--ts-primary);
  color: #ffffff;
  background: var(--ts-primary);
}

.fee-option > span:nth-child(2) {
  font-size: 12px;
  font-weight: 680;
}

.fee-option > strong {
  color: #ffffff;
  font-size: 20px;
  font-weight: 770;
  letter-spacing: -0.025em;
}

.fee-option--custom {
  align-content: start;
}

.fee-option--custom > span:nth-child(2) {
  margin: 4px 0 5px;
}

.fee-input {
  width: 100%;
  margin: 0 !important;
}

.fee-input ::v-deep input {
  text-align: center;
}

.feedback-fields {
  margin-top: 12px;
  margin-bottom: -12px !important;
}

.payment-section .section-heading {
  align-items: center;
}

.payment-amount {
  flex: 0 0 auto;
  margin-left: auto;
  color: #ffffff;
  font-size: 22px;
  font-weight: 770;
  letter-spacing: -0.025em;
}

.payment-actions,
.application-actions__secondary,
.application-actions__primary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.payment-actions {
  margin-bottom: 14px;
}

.invoice-banner {
  display: flex;
  min-height: 70px;
  align-items: center;
  gap: 12px;
  padding: 13px 15px;
  border: 1px solid rgba(0, 150, 64, 0.2);
  border-radius: 15px;
  background: rgba(0, 150, 64, 0.075);
}

.invoice-banner__icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
}

.invoice-banner > div {
  display: grid;
  gap: 2px;
}

.invoice-banner span {
  color: #8f8f94;
  font-size: 10px;
  font-weight: 750;
  text-transform: uppercase;
}

.invoice-banner strong {
  color: #e5e5e7;
  font-size: 13px;
}

.application-actions {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.13);
}

.application-actions__primary {
  justify-content: flex-end;
}

.secondary-action {
  border-color: rgba(255, 255, 255, 0.14) !important;
  color: #c5c5c9 !important;
}

.secondary-action:hover,
.secondary-action:focus-visible {
  border-color: rgba(0, 150, 64, 0.34) !important;
  color: #e9f7ee !important;
  background: rgba(0, 150, 64, 0.09);
}

.primary-outline-action {
  border-color: rgba(0, 150, 64, 0.42) !important;
  color: #65ca8b !important;
}

.primary-outline-action:hover,
.primary-outline-action:focus-visible {
  color: #ffffff !important;
  background: rgba(0, 150, 64, 0.14);
}

.secondary-solid-action {
  color: #eeeeef !important;
  background: #3a3a3c !important;
  box-shadow: none;
}

.danger-action {
  color: #ef8585 !important;
}

.danger-action:hover,
.danger-action:focus-visible {
  color: #ffffff !important;
  background: rgba(220, 58, 70, 0.13);
}

.primary-action.v-btn--disabled,
.secondary-solid-action.v-btn--disabled {
  color: rgba(255, 255, 255, 0.34) !important;
  background: rgba(255, 255, 255, 0.07) !important;
  box-shadow: none;
}

.manual-status {
  display: grid;
  grid-template-columns: minmax(170px, 1fr) minmax(210px, 1.3fr) auto;
  align-items: center;
  gap: 12px;
  padding: 15px;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.1);
}

.manual-status > div {
  display: grid;
  gap: 2px;
}

.manual-status strong {
  color: #d6d6d9;
  font-size: 12px;
}

.manual-status span {
  color: #85858a;
  font-size: 10px;
}

.dialog-card {
  --ts-primary: #009640;
  --ts-primary-hover: #00a848;
  --ts-primary-soft: rgba(0, 150, 64, 0.14);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  border-radius: 24px !important;
  color: var(--ts-text, #f4f4f5);
  background: radial-gradient(circle at 91% -8%, rgba(0, 150, 64, 0.09), transparent 23rem), #202021 !important;
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.58) !important;
}

.dialog-header {
  display: flex;
  flex: 0 0 auto;
  min-height: 108px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  padding: 22px 24px !important;
  background: rgba(0, 0, 0, 0.2);
}

.dialog-header--compact {
  min-height: 84px;
  align-items: center;
  padding-block: 17px !important;
}

.dialog-header__icon {
  width: 46px;
  height: 46px;
  border-radius: 15px;
}

.dialog-header h2 {
  margin: 0;
  color: #ffffff;
  font-size: 22px;
  font-weight: 750;
  letter-spacing: -0.025em;
  line-height: 1.2;
}

.dialog-header p {
  margin: 4px 0 0;
  color: #98989d;
  font-size: 12px;
  font-weight: 400;
}

.icon-action {
  flex: 0 0 auto;
  width: 44px !important;
  min-width: 44px !important;
  height: 44px !important;
  border-radius: 13px;
  color: #b8b8bd !important;
}

.icon-action:hover,
.icon-action:focus-visible {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.07);
}

.dialog-divider {
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.dialog-content {
  max-height: min(72vh, 820px);
  padding: 20px 22px 24px !important;
}

.date-picker-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.dialog-section {
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 17px;
  background: rgba(255, 255, 255, 0.03);
}

.dialog-section__label {
  display: flex;
  min-height: 32px;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  color: #d0d0d3;
  font-size: 12px;
  font-weight: 700;
}

.dialog-section__label .v-icon {
  color: #65ca8b;
}

.dialog-section ::v-deep .v-picker {
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  background: rgba(0, 0, 0, 0.14) !important;
  box-shadow: none;
}

.dialog-section ::v-deep .v-date-picker-table .v-btn.v-btn--active {
  color: #ffffff !important;
  background: var(--ts-primary) !important;
}

.fee-edit-section {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(180px, 260px);
  align-items: center;
  gap: 18px;
  margin-top: 12px;
}

.fee-edit-section > div {
  display: grid;
  gap: 3px;
}

.fee-edit-section strong {
  color: #e6e6e8;
  font-size: 13px;
}

.fee-edit-section span {
  color: #8f8f94;
  font-size: 11px;
  line-height: 1.45;
}

.dialog-footer {
  flex: 0 0 auto;
  min-height: 74px;
  gap: 8px;
  padding: 13px 18px !important;
  background: rgba(18, 18, 19, 0.92);
  backdrop-filter: blur(16px);
}

.image-preview-surface {
  display: flex;
  min-height: 360px;
  max-height: 72vh;
  align-items: center;
  justify-content: center;
  padding: 14px;
  background: linear-gradient(45deg, rgba(255, 255, 255, 0.02) 25%, transparent 25%, transparent 75%, rgba(255, 255, 255, 0.02) 75%), #171718;
  background-position: 0 0, 10px 10px;
  background-size: 20px 20px;
}

.full-size-image {
  width: 100%;
  height: min(66vh, 680px);
}

.embedded-dialog-card,
.calculator-dialog-card {
  height: 100%;
}

.embedded-dialog-content {
  max-height: min(78vh, 820px);
  padding: 16px !important;
  background: rgba(0, 0, 0, 0.08);
}

.embedded-dialog-content ::v-deep > .v-card,
.calculator-dialog-content ::v-deep > .v-card {
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.calculator-dialog-content {
  flex: 1 1 auto;
  height: calc(100vh - 85px);
  padding: 0 !important;
  overflow: auto;
  background: #1c1c1d;
}

.timestop-page ::v-deep .v-btn:focus-visible,
.timestop-page ::v-deep .v-expansion-panel-header:focus-visible,
.dialog-card ::v-deep .v-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.25) !important;
}

@media (max-width: 959px) {
  .page-hero {
    min-height: 138px;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .fee-options {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .fee-option--custom {
    grid-column: 1 / -1;
  }

  .application-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .application-actions__primary {
    justify-content: flex-start;
  }

  .manual-status {
    grid-template-columns: 1fr 1fr;
  }

  .manual-status > div {
    grid-column: 1 / -1;
  }

  .dialog-card {
    border: 0 !important;
    border-radius: 0 !important;
  }

  .dialog-content,
  .embedded-dialog-content {
    max-height: none;
  }
}

@media (max-width: 719px) {
  .date-picker-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 599px) {
  .timestop-page {
    padding: 10px;
  }

  .page-hero {
    align-items: stretch;
    flex-direction: column;
    gap: 18px;
    padding: 20px 17px;
    border-radius: 20px;
  }

  .page-hero__copy {
    align-items: flex-start;
    gap: 12px;
  }

  .page-hero__icon {
    width: 44px;
    height: 44px;
    border-radius: 14px;
  }

  .page-hero h1 {
    font-size: 27px;
  }

  .page-hero p {
    font-size: 12px;
  }

  .hero-action {
    width: 100%;
  }

  .summary-grid,
  .filter-surface,
  .timestop-panels,
  .loading-state,
  .empty-state {
    margin-top: 10px;
  }

  .summary-card {
    min-height: 86px;
    padding: 14px;
    border-radius: 16px;
  }

  .filter-surface {
    padding: 15px;
    border-radius: 18px;
  }

  .surface-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .quick-actions {
    display: grid;
    grid-template-columns: 1fr;
  }

  .quick-actions__label {
    margin-bottom: 2px;
  }

  .quick-action {
    width: 100%;
    justify-content: flex-start;
  }

  .loading-state,
  .empty-state {
    align-items: flex-start;
    padding: 20px;
  }

  .timestop-panel {
    border-radius: 17px !important;
  }

  .timestop-panel__header {
    min-height: 78px;
    padding: 12px !important;
  }

  .timestop-header {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 10px;
  }

  .timestop-header__copy {
    max-width: calc(100% - 54px);
  }

  .timestop-header__chips {
    width: 100%;
    margin-left: 0;
    justify-content: flex-start;
  }

  .application-content {
    padding: 0 10px 10px;
  }

  .detail-grid {
    padding-top: 10px;
  }

  .detail-section {
    padding: 14px;
    border-radius: 15px;
  }

  .period-card {
    grid-template-columns: 1fr;
    text-align: left;
  }

  .period-card > div:last-child {
    text-align: left;
  }

  .period-card > .v-icon {
    transform: rotate(90deg);
  }

  .signature-card {
    grid-template-columns: 1fr;
  }

  .fee-options {
    grid-template-columns: 1fr;
  }

  .fee-option--custom {
    grid-column: auto;
  }

  .fee-option {
    min-height: 112px;
  }

  .payment-section .section-heading {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .payment-amount {
    width: calc(100% - 44px);
    margin: 5px 0 0 44px;
  }

  .payment-actions,
  .application-actions__secondary,
  .application-actions__primary {
    display: grid;
    grid-template-columns: 1fr;
    width: 100%;
  }

  .payment-actions .v-btn,
  .application-actions .v-btn {
    width: 100%;
  }

  .manual-status {
    grid-template-columns: 1fr;
  }

  .manual-status > div {
    grid-column: auto;
  }

  .dialog-header {
    min-height: auto;
    padding: 17px 15px !important;
  }

  .dialog-header__copy {
    align-items: flex-start;
    gap: 10px;
  }

  .dialog-header__icon {
    width: 40px;
    height: 40px;
    border-radius: 13px;
  }

  .dialog-header h2 {
    font-size: 19px;
  }

  .dialog-header p {
    font-size: 11px;
  }

  .dialog-content {
    padding: 13px !important;
  }

  .date-picker-grid {
    grid-template-columns: 1fr;
  }

  .dialog-section {
    padding: 13px;
    border-radius: 15px;
  }

  .fee-edit-section {
    grid-template-columns: 1fr;
  }

  .dialog-footer {
    min-height: auto;
    padding: 10px 10px calc(10px + env(safe-area-inset-bottom)) !important;
  }

  .dialog-footer .v-spacer {
    display: none;
  }

  .dialog-footer .v-btn {
    flex: 1 1 0;
  }

  .image-preview-surface {
    min-height: calc(100vh - 78px);
    padding: 8px;
  }

  .full-size-image {
    height: calc(100vh - 94px);
  }

  .embedded-dialog-content {
    padding: 10px !important;
  }
}

@media (max-width: 479px) {
  .dialog-footer {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .dialog-footer .v-btn {
    width: 100%;
    flex: 0 0 auto;
  }
}
</style>
